import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useAuth } from './useAuth';
import { eventNotificationService, companyNotificationService } from '../services/eventNotificationService';

interface NotificationContextType {
  unreadCount: number;
  setUnreadCount: (count: number) => void;
  decrementUnread: () => void;
  resetUnread: () => void;
  refetch: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const isAdmin   = user?.userRole === 'admin';
  const isCompany = user?.userRole === 'company_admin' || user?.userRole === 'hr';
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!isAdmin && !isCompany) return;
    try {
      const service = isAdmin ? eventNotificationService : companyNotificationService;
      const count = await service.getUnreadCount();
      setUnreadCount(count);
    } catch {
      // ポーリングエラーはサイレントに無視
    }
  }, [isAdmin, isCompany]);

  useEffect(() => {
    if (!isAdmin && !isCompany) return;

    fetchUnreadCount();
    const intervalId = setInterval(fetchUnreadCount, 60_000);

    return () => clearInterval(intervalId);
  }, [isAdmin, isCompany, fetchUnreadCount]);

  const decrementUnread = useCallback(() => {
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);

  const resetUnread = useCallback(() => {
    setUnreadCount(0);
  }, []);

  return (
    <NotificationContext.Provider value={{
      unreadCount,
      setUnreadCount,
      decrementUnread,
      resetUnread,
      refetch: fetchUnreadCount,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotificationContext = (): NotificationContextType => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotificationContext must be used within NotificationProvider');
  return ctx;
};
